import { datadogLogs } from '@mark43/datadog';
import { sanitizeMessageWithMask } from '@mark43/log-lib-ts';

type LogContext = Record<string, unknown>;
const isDev = process.env.NODE_ENV === 'development';

type SingleItemLog = (message: string, context?: LogContext) => void;

/**
 * Recursively sanitizes all string values in an object using sanitizeMessageWithMask.
 * Returns a new object with sanitized strings, preserving the original structure.
 */
export function sanitizeContext(value: unknown): unknown {
    if (typeof value === 'string') {
        return sanitizeMessageWithMask(value);
    }

    if (Array.isArray(value)) {
        return value.map(sanitizeContext);
    }

    if (value !== null && typeof value === 'object') {
        const sanitized: Record<string, unknown> = {};
        for (const key in value as Record<string, unknown>) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                sanitized[key] = sanitizeContext((value as Record<string, unknown>)[key]);
            }
        }
        return sanitized;
    }

    return value;
}

// Module-level state for logging configuration
let isInfoLoggingEnabled = false;
let isDebugLoggingEnabled = false;
let isDatadogEnabled = false;

/**
 * Sets the logging configuration based on feature flags.
 * This should be called once during app bootstrap.
 *
 * @param config Configuration object with logging flags
 */
export function setLoggingConfig(config: {
    feLoggingEnabled: boolean;
    feDebugLoggingEnabled: boolean;
    datadogEnabled: boolean;
}): void {
    isInfoLoggingEnabled = config.feLoggingEnabled;
    isDebugLoggingEnabled = config.feDebugLoggingEnabled;
    isDatadogEnabled = config.datadogEnabled;
}

export const logDebug: SingleItemLog = (message, context) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.debug(message, context);
    }
    const sanitizedMessage = sanitizeMessageWithMask(message);
    if (!isDebugLoggingEnabled || !sanitizedMessage) {
        return;
    }
    const sanitizedContext = context ? (sanitizeContext(context) as LogContext) : undefined;

    if (isDatadogEnabled) {
        datadogLogs.logger.debug(sanitizedMessage, sanitizedContext);
    }
};

export const logInfo: SingleItemLog = (message, context) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.info(message, context);
    }
    const sanitizedMessage = sanitizeMessageWithMask(message);
    if (!isInfoLoggingEnabled || !sanitizedMessage) {
        return;
    }
    const sanitizedContext = context ? (sanitizeContext(context) as LogContext) : undefined;

    if (isDatadogEnabled) {
        datadogLogs.logger.info(sanitizedMessage, sanitizedContext);
    }
};

export const logWarn: SingleItemLog = (message, context) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(message, context);
    }
    const sanitizedMessage = sanitizeMessageWithMask(message);
    if (!isInfoLoggingEnabled || !sanitizedMessage) {
        return;
    }
    const sanitizedContext = context ? (sanitizeContext(context) as LogContext) : undefined;

    if (isDatadogEnabled) {
        datadogLogs.logger.warn(sanitizedMessage, sanitizedContext);
    }
};

export const logError = (error: Error, context?: LogContext) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.error(error, context);
    }
    const sanitizedMessage = sanitizeMessageWithMask(error.message);
    const sanitizedContext = context ? (sanitizeContext(context) as LogContext) : {};
    if (!isInfoLoggingEnabled || !sanitizedMessage) {
        return;
    }

    const errorContext = {
        error_name: error.name,
        error_stack: error.stack,
        ...sanitizedContext,
    };

    if (isDatadogEnabled) {
        datadogLogs.logger.error(sanitizedMessage, errorContext, error);
    }
};

export const logErrorMessage: SingleItemLog = (errorMessage, context) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.error(errorMessage, context);
    }
    const sanitizedMessage = sanitizeMessageWithMask(errorMessage);
    const error = new Error(errorMessage);
    const sanitizedContext = context ? (sanitizeContext(context) as LogContext) : {};
    if (!isInfoLoggingEnabled || !sanitizedMessage) {
        return;
    }

    const errorContext = {
        error_name: error.name,
        error_stack: error.stack,
        ...sanitizedContext,
    };

    if (isDatadogEnabled) {
        datadogLogs.logger.error(sanitizedMessage, errorContext, error);
    }
};
